
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function Services() {
  const services = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Project Consultancy & Specification Support',
      description: 'Helping engineers and consultants design long-lasting systems with expert technical guidance.',
      color: '#45524A',
      features: ['Technical Specifications', 'System Design', 'Performance Analysis', 'Cost Optimization']
    },
    {
      icon: 'ri-tools-fill',
      title: 'On-site Technical Assistance',
      description: 'Quality checks, audits, troubleshooting, and supervision for critical applications.',
      color: '#45524A',
      features: ['Quality Audits', 'Application Supervision', 'Troubleshooting', 'Performance Monitoring']
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Training & Capacity Building',
      description: 'Practical training for applicators, contractors, and project teams.',
      color: '#45524A',
      features: ['Hands-on Training', 'Certification Programs', 'Safety Protocols', 'Best Practices']
    },
    {
      icon: 'ri-links-line',
      title: 'Integrated Supply Chain',
      description: 'Coordinating multiple manufacturers to ensure continuity, alternative sourcing, and material buffers.',
      color: '#45524A',
      features: ['Supply Continuity', 'Alternative Sourcing', 'Inventory Management', 'Logistics Coordination']
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Customized Solutions',
      description: 'Offering tailor-made chemical solutions based on project size, climate, and application requirements.',
      color: '#45524A',
      features: ['Custom Formulations', 'Climate Adaptation', 'Project-specific Solutions', 'Performance Optimization']
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Assurance & Testing',
      description: 'Comprehensive testing services and quality control measures for all chemical applications.',
      color: '#45524A',
      features: ['Material Testing', 'Quality Control', 'Performance Validation', 'Compliance Verification']
    }
  ];

  const capabilities = [
    {
      title: 'Technical Expertise',
      description: 'Our team of certified professionals brings years of experience in construction chemistry with deep knowledge of material science and application techniques.',
      image: 'Professional engineering team in modern office environment reviewing construction project blueprints and technical specifications, contemporary workspace with large windows and natural lighting',
      stats: { value: '25+', label: 'Years Experience' }
    },
    {
      title: 'Quality Assurance',
      description: 'Rigorous testing and quality control processes ensure every product meets international standards with comprehensive documentation and certification.',
      image: 'Modern quality control facility with advanced testing equipment and professional technicians conducting material analysis, clean industrial environment with precision instruments',
      stats: { value: '99.8%', label: 'Quality Rating' }
    },
    {
      title: 'Project Management',
      description: 'End-to-end project support from planning to execution and post-completion maintenance with dedicated project coordinators.',
      image: 'Construction project management team coordinating on large infrastructure site, professional project managers with safety equipment overseeing construction progress',
      stats: { value: '500+', label: 'Projects Completed' }
    },
    {
      title: 'Pan-India Network',
      description: 'Extensive distribution network across India ensuring timely delivery and local support for all your construction chemical needs.',
      image: 'Modern distribution center with organized warehouse facilities and logistics vehicles, efficient supply chain operations with professional staff',
      stats: { value: '50+', label: 'Distribution Centers' }
    }
  ];

  const specializedServices = [
    {
      category: 'Waterproofing Solutions',
      icon: 'ri-drop-line',
      services: [
        'Basement Waterproofing',
        'Roof & Terrace Protection',
        'Swimming Pool Waterproofing',
        'Tunnel & Underground Structures'
      ]
    },
    {
      category: 'Structural Repair',
      icon: 'ri-hammer-line',
      services: [
        'Concrete Repair & Strengthening',
        'Crack Injection Systems',
        'Structural Grouting',
        'Carbon Fiber Reinforcement'
      ]
    },
    {
      category: 'Flooring Systems',
      icon: 'ri-building-line',
      services: [
        'Industrial Floor Coatings',
        'Anti-static Flooring',
        'Chemical Resistant Floors',
        'Decorative Concrete Systems'
      ]
    },
    {
      category: 'Protective Coatings',
      icon: 'ri-paint-brush-line',
      services: [
        'Anti-corrosion Coatings',
        'Fire Retardant Systems',
        'Marine Protection',
        'Industrial Maintenance Coatings'
      ]
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'Understanding your project requirements, challenges, and specific needs through detailed discussion.',
      icon: 'ri-discuss-line'
    },
    {
      step: '02',
      title: 'Site Assessment',
      description: 'Comprehensive on-site evaluation to analyze conditions and determine optimal solutions.',
      icon: 'ri-search-eye-line'
    },
    {
      step: '03',
      title: 'Solution Design',
      description: 'Customizing chemical solutions and application methods for optimal performance and durability.',
      icon: 'ri-draft-line'
    },
    {
      step: '04',
      title: 'Implementation',
      description: 'Professional application with on-site supervision, quality control, and safety compliance.',
      icon: 'ri-tools-line'
    },
    {
      step: '05',
      title: 'Quality Verification',
      description: 'Thorough testing and inspection to ensure all specifications and standards are met.',
      icon: 'ri-check-double-line'
    },
    {
      step: '06',
      title: 'Ongoing Support',
      description: 'Continuous maintenance guidance, technical support, and performance monitoring.',
      icon: 'ri-customer-service-2-line'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-20 min-h-[80vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20construction%20site%20with%20professional%20engineers%20and%20project%20managers%20coordinating%20work%2C%20contemporary%20architecture%20and%20infrastructure%20development%2C%20business%20professionals%20in%20safety%20equipment&width=1920&height=1080&seq=services-hero&orientation=landscape')`
          }}
        >
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(69, 82, 74, 0.85)' }}></div>
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <span className="text-sm font-semibold">Comprehensive Service Portfolio</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Expert Services &
              <span className="block text-emerald-300">Technical Solutions</span>
            </h1>
            <p className="text-xl leading-relaxed mb-8 max-w-3xl mx-auto">
              We are more than distributors — we are <strong>solution providers</strong> offering comprehensive technical expertise, quality assurance, and end-to-end project support across India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                Explore Our Services
              </button>
              <button className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
                Get Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#45524A' }}>Our Core Services</h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">From consultation to execution, we provide end-to-end support for your construction chemical needs with technical expertise and quality assurance</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-t-4" style={{ borderColor: '#45524A' }}>
                  <div 
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}
                  >
                    <i className={`${service.icon} text-3xl`} style={{ color: service.color }}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                        <i className="ri-check-line text-emerald-500 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#45524A' }}>Specialized Service Categories</h2>
              <p className="text-gray-600 text-lg">Comprehensive solutions across all construction chemical applications</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {specializedServices.map((category, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                  <div 
                    className="w-14 h-14 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: '#45524A' }}
                  >
                    <i className={`${category.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#45524A' }}>{category.category}</h3>
                  <ul className="space-y-2">
                    {category.services.map((service, serviceIndex) => (
                      <li key={serviceIndex} className="text-sm text-gray-600 flex items-center">
                        <i className="ri-arrow-right-s-line text-gray-400 mr-1"></i>
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#45524A' }}>Excellence Through Innovation &amp; Expertise</h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Our comprehensive capabilities combine cutting-edge technology, proven methodologies, and deep industry expertise to deliver exceptional results across every project phase.
              </p>
            </div>

            {/* Technical Expertise */}
            <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
              <div>
                <div className="flex items-center mb-4">
                  <div className="px-6 py-2 rounded-full text-white font-bold text-lg mr-4" style={{ backgroundColor: '#45524A' }}>25+</div>
                  <span className="text-gray-600 font-medium">Years Experience</span>
                </div>
                <h3 className="text-3xl font-bold mb-6" style={{ color: '#45524A' }}>Advanced Technical Expertise</h3>
                <p className="text-gray-600 leading-relaxed text-lg mb-6">
                  Our team of certified professionals brings decades of experience in construction chemistry with deep knowledge of material science, application techniques, and innovative solutions for complex engineering challenges.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">Certified Material Scientists &amp; Chemical Engineers</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">Advanced Laboratory Testing &amp; Analysis</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">Continuous R&amp;D and Innovation Programs</span>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4" style={{ borderColor: '#45524A' }}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-award-line text-2xl" style={{ color: '#45524A' }}></i>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-800">Industry Recognition</span>
                      <p className="text-sm text-gray-600">Multiple certifications and awards for technical excellence</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20engineering%20team%20working%20with%20advanced%20construction%20blueprints%20and%20technical%20drawings%2C%20modern%20office%20environment%20with%20engineers%20analyzing%20structural%20plans%2C%20construction%20project%20planning%20with%20technical%20documentation%2C%20professional%20architects%20and%20engineers%20collaborating%20on%20building%20designs&width=600&height=400&seq=technical-expertise&orientation=landscape"
                  alt="Technical Expertise"
                  className="rounded-xl shadow-lg object-cover w-full h-80"
                />
              </div>
            </div>

            {/* Quality Assurance */}
            <div className="grid md:grid-cols-2 gap-12 items-center mb-20 md:grid-flow-col-dense">
              <div className="md:col-start-2">
                <div className="flex items-center mb-4">
                  <div className="px-6 py-2 rounded-full text-white font-bold text-lg mr-4" style={{ backgroundColor: '#45524A' }}>99.8%</div>
                  <span className="text-gray-600 font-medium">Quality Rating</span>
                </div>
                <h3 className="text-3xl font-bold mb-6" style={{ color: '#45524A' }}>Comprehensive Quality Assurance</h3>
                <p className="text-gray-600 leading-relaxed text-lg mb-6">
                  Our rigorous quality control processes ensure every product meets international standards through comprehensive testing, documentation, and certification protocols that exceed industry benchmarks.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">ISO 9001:2015 Certified Quality Management</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">Multi-stage Testing &amp; Validation Protocols</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">Real-time Performance Monitoring</span>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-green-100">
                      <i className="ri-shield-check-line text-2xl text-green-600"></i>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-800">Zero Defect Commitment</span>
                      <p className="text-sm text-gray-600">Guaranteed performance with comprehensive warranties</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:col-start-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20quality%20control%20inspector%20examining%20construction%20materials%20with%20precision%20measuring%20instruments%2C%20modern%20quality%20assurance%20laboratory%20with%20testing%20equipment%2C%20construction%20material%20certification%20process%2C%20professional%20inspector%20with%20safety%20gear%20checking%20building%20standards&width=600&height=400&seq=quality-assurance&orientation=landscape"
                  alt="Quality Assurance"
                  className="rounded-xl shadow-lg object-cover w-full h-80"
                />
              </div>
            </div>

            {/* Project Management */}
            <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
              <div>
                <div className="flex items-center mb-4">
                  <div className="px-6 py-2 rounded-full text-white font-bold text-lg mr-4" style={{ backgroundColor: '#45524A' }}>500+</div>
                  <span className="text-gray-600 font-medium">Projects Completed</span>
                </div>
                <h3 className="text-3xl font-bold mb-6" style={{ color: '#45524A' }}>Strategic Project Management</h3>
                <p className="text-gray-600 leading-relaxed text-lg mb-6">
                  End-to-end project support from initial planning to execution and post-completion maintenance with dedicated project coordinators ensuring seamless delivery and optimal outcomes.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">Dedicated Project Coordinators &amp; Teams</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">Advanced Project Tracking &amp; Monitoring</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">Risk Management &amp; Contingency Planning</span>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-blue-100">
                      <i className="ri-time-line text-2xl text-blue-600"></i>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-800">On-Time Delivery</span>
                      <p className="text-sm text-gray-600">98% projects completed within scheduled timelines</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20construction%20project%20manager%20coordinating%20with%20team%20members%20at%20construction%20site%2C%20modern%20building%20project%20with%20safety%20helmets%20and%20blueprints%2C%20construction%20management%20meeting%20with%20engineers%20and%20architects%2C%20project%20coordination%20with%20digital%20tablets%20and%20planning%20documents&width=600&height=400&seq=project-management&orientation=landscape"
                  alt="Project Management"
                  className="rounded-xl shadow-lg object-cover w-full h-80"
                />
              </div>
            </div>

            {/* Pan-India Network */}
            <div className="grid md:grid-cols-2 gap-12 items-center mb-20 md:grid-flow-col-dense">
              <div className="md:col-start-2">
                <div className="flex items-center mb-4">
                  <div className="px-6 py-2 rounded-full text-white font-bold text-lg mr-4" style={{ backgroundColor: '#45524A' }}>50+</div>
                  <span className="text-gray-600 font-medium">Distribution Centers</span>
                </div>
                <h3 className="text-3xl font-bold mb-6" style={{ color: '#45524A' }}>Extensive Pan-India Network</h3>
                <p className="text-gray-600 leading-relaxed text-lg mb-6">
                  Our comprehensive distribution network spans across India, ensuring timely delivery and localized support for all construction chemical requirements with strategic warehouses and regional expertise.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">15+ States with Direct Operations</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">Regional Technical Support Centers</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                      <i className="ri-check-line text-lg" style={{ color: '#45524A' }}></i>
                    </div>
                    <span className="text-gray-700">24/7 Logistics &amp; Supply Chain Management</span>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-purple-500">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-purple-100">
                      <i className="ri-truck-line text-2xl text-purple-600"></i>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-800">Rapid Response</span>
                      <p className="text-sm text-gray-600">Same-day dispatch for urgent requirements</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:col-start-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20logistics%20and%20distribution%20center%20with%20trucks%20loading%20construction%20materials%2C%20professional%20warehouse%20operations%20with%20organized%20inventory%20management%2C%20supply%20chain%20coordination%20with%20delivery%20vehicles%2C%20efficient%20distribution%20network%20with%20workers%20managing%20shipments&width=600&height=400&seq=distribution-network&orientation=landscape"
                  alt="Pan-India Network"
                  className="rounded-xl shadow-lg object-cover w-full h-80"
                />
              </div>
            </div>

            {/* Additional Capabilities */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-8 text-center" style={{ color: '#45524A' }}>Additional Core Strengths</h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                    <i className="ri-customer-service-2-line text-2xl" style={{ color: '#45524A' }}></i>
                  </div>
                  <h4 className="text-xl font-semibold mb-3" style={{ color: '#45524A' }}>24/7 Support</h4>
                  <p className="text-gray-600">Round-the-clock technical assistance and emergency response for critical projects</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                    <i className="ri-graduation-cap-line text-2xl" style={{ color: '#45524A' }}></i>
                  </div>
                  <h4 className="text-xl font-semibold mb-3" style={{ color: '#45524A' }}>Training Programs</h4>
                  <p className="text-gray-600">Comprehensive training and certification programs for applicators and technical staff</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}>
                    <i className="ri-settings-3-line text-2xl" style={{ color: '#45524A' }}></i>
                  </div>
                  <h4 className="text-xl font-semibold mb-3" style={{ color: '#45524A' }}>Custom Solutions</h4>
                  <p className="text-gray-600">Tailored chemical formulations and application methods for unique project requirements</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#45524A' }}>Our Service Process</h2>
              <p className="text-gray-600 text-lg">A systematic approach ensuring quality and success at every step</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                    <div className="flex items-center mb-6">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4"
                        style={{ backgroundColor: '#45524A' }}
                      >
                        {step.step}
                      </div>
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(69, 82, 74, 0.1)' }}
                      >
                        <i className={`${step.icon} text-lg`} style={{ color: '#45524A' }}></i>
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#45524A' }}>{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <i className="ri-arrow-right-line text-2xl text-gray-300"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Client Reviews Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#45524A' }}>
                Client Success Stories
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                Hear from our satisfied clients about their experience with our comprehensive 
                construction chemical services and technical expertise.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  name: "Sanjay Gupta",
                  position: "Construction Manager",
                  company: "DLF Limited",
                  review: "Lavin's waterproofing solutions and technical support have been exceptional for our residential projects. Their team's expertise and on-site guidance ensured perfect application and long-term performance.",
                  rating: 5,
                  project: "Residential Waterproofing",
                  image: "Professional Indian construction manager at residential building site, experienced project manager with safety helmet and construction plans, modern apartment complex background with construction equipment"
                },
                {
                  name: "Neha Patel",
                  position: "Site Engineer",
                  company: "L&T Construction",
                  review: "The structural strengthening services provided by Lavin Enterprises exceeded our expectations. Their innovative solutions and technical guidance helped us complete our infrastructure project ahead of schedule.",
                  rating: 5,
                  project: "Infrastructure Strengthening",
                  image: "Professional Indian female site engineer at infrastructure construction site, confident engineering professional with safety gear and technical drawings, bridge construction background with heavy machinery"
                },
                {
                  name: "Rohit Sharma",
                  position: "Quality Manager",
                  company: "Ambuja Cement",
                  review: "Working with Lavin for our concrete admixture requirements has been outstanding. Their consistent quality and technical support have significantly improved our concrete performance and durability.",
                  rating: 5,
                  project: "Concrete Enhancement",
                  image: "Professional Indian quality manager at concrete plant facility, experienced quality control professional with testing equipment and safety gear, modern concrete production facility background"
                },
                {
                  name: "Priya Krishnan",
                  position: "Project Director",
                  company: "Godrej Properties",
                  review: "Lavin's comprehensive flooring solutions for our commercial projects have been remarkable. Their technical team provided excellent support throughout the application process, ensuring superior results.",
                  rating: 5,
                  project: "Commercial Flooring",
                  image: "Professional Indian female project director at commercial construction site, confident project management professional in formal attire, modern office building construction background"
                },
                {
                  name: "Arun Kumar",
                  position: "Technical Head",
                  company: "BHEL",
                  review: "The industrial chemical solutions provided by Lavin have proven highly effective for our manufacturing facilities. Their expertise in chemical-resistant applications is truly commendable.",
                  rating: 5,
                  project: "Industrial Solutions",
                  image: "Professional Indian technical head at industrial facility, experienced technical professional with safety equipment, modern manufacturing plant background with industrial machinery"
                },
                {
                  name: "Kavita Singh",
                  position: "Operations Manager",
                  company: "Prestige Group",
                  review: "Lavin's repair and rehabilitation services have been instrumental in maintaining our property portfolio. Their innovative solutions and professional approach have consistently delivered excellent results.",
                  rating: 5,
                  project: "Building Rehabilitation",
                  image: "Professional Indian operations manager at building renovation site, experienced facility management professional with inspection tools, building restoration project background"
                }
              ].map((review, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 rounded-full overflow-hidden mr-4 flex-shrink-0">
                      <img 
                        src={`https://readdy.ai/api/search-image?query=$%7Breview.image%7D&width=150&height=150&seq=services-client-${index}&orientation=squarish`}
                        alt={review.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">{review.name}</h3>
                      <p className="text-sm text-gray-600">{review.position}</p>
                      <p className="text-sm font-medium" style={{ color: '#45524A' }}>{review.company}</p>
                    </div>
                  </div>

                  <div className="flex mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                    ))}
                  </div>

                  <p className="text-gray-700 leading-relaxed mb-4 italic">
                    "{review.review}"
                  </p>

                  <div className="border-t pt-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <i className="ri-tools-line mr-2"></i>
                      <span>{review.project}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Service Excellence Stats */}
            <div className="mt-16 bg-white rounded-2xl p-8 md:p-12 shadow-lg">
              <h3 className="text-3xl font-bold mb-8 text-center" style={{ color: '#45524A' }}>
                Service Excellence
              </h3>
              <div className="grid md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold mb-2" style={{ color: '#45524A' }}>24/7</div>
                  <div className="text-gray-600">Technical Support</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2" style={{ color: '#45524A' }}>98%</div>
                  <div className="text-gray-600">On-Time Delivery</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2" style={{ color: '#45524A' }}>500+</div>
                  <div className="text-gray-600">Service Projects</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2" style={{ color: '#45524A' }}>15+</div>
                  <div className="text-gray-600">Service Categories</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(to right, rgba(69, 82, 74, 0.1), rgba(69, 82, 74, 0.05))' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#45524A' }}>Ready to Start Your Project?</h2>
            <p className="text-xl text-gray-600 mb-8">
              Get expert consultation and customized solutions for your construction chemical needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105"
                style={{ backgroundColor: '#45524A' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#3A453D'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#45524A'}
              >
                Get Free Consultation
              </button>
              <button className="px-8 py-4 text-lg font-semibold border-2 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap hover:bg-gray-50" style={{ borderColor: '#45524A', color: '#45524A' }}>
                Download Service Brochure
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
